import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Percent, Save, Loader2, Info } from "lucide-react";
import { toast } from "sonner";

export default function SelfCalibration({ propId, lang = "it" }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [pct, setPct] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!propId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${propId}/self-calibration`);
      setData(data);
      setPct(data.self_calibration_pct != null ? String(data.self_calibration_pct) : "");
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [propId]);

  const save = async (clear = false) => {
    setSaving(true);
    try {
      await api.put(`/properties/${propId}/self-calibration`, { pct: clear ? null : Number(pct) });
      toast.success(clear ? L("Correzione rimossa", "Correction cleared") : L("Correzione salvata", "Correction saved"));
      if (clear) setPct("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || L("Errore nel salvataggio", "Save error"));
    } finally { setSaving(false); }
  };

  const gross = data?.self_price;
  const p = Number(pct);
  const net = gross && !isNaN(p) && p >= 0 ? Math.round(gross * (1 - p / 100)) : null;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border rounded-lg p-6" data-testid="self-calibration">
      <div className="flex items-center gap-2 mb-1">
        <Percent className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-semibold">{L("Correzione prezzo della tua struttura", "Your property price correction")}</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        {L("Inserisci la percentuale di sconti/offerte attive sulla tua tariffa (es. Genius, promo). Il motore la userà come sconto reale per calcolare il tuo prezzo NETTO e confrontarlo con i netti dei competitor (limati da te nella Calibrazione Competitor).",
          "Enter the % of active discounts/offers on your rate (e.g. Genius, promo). The engine uses it as your real discount to compute your NET price and compare it against competitors' net prices.")}
      </p>

      {loading ? (
        <div className="py-6 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : (
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col">
            <label className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-1">{L("Sconto/Offerta %", "Discount/Offer %")}</label>
            <div className="flex items-center gap-2">
              <input data-testid="self-calibration-input" type="number" min="0" max="45" step="0.5"
                value={pct} onChange={(e) => setPct(e.target.value)} placeholder="0"
                className="h-10 w-28 rounded-md border border-border bg-background px-3 text-sm font-data" />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-1">{L("Tua tariffa lorda", "Your gross rate")}</span>
            <span className="h-10 flex items-center font-data text-lg">
              {gross ? `€${Math.round(gross)}` : "—"}
              {gross && data?.self_price_provisional && (
                <span className="ml-2 text-[10px] font-sans font-normal normal-case text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" title={L("Prezzo base configurato: verrà aggiornato con il prezzo reale letto da Booking dopo la scansione mercato.", "Configured base price: will update with the real Booking price after a market scan.")}>
                  {L("provvisorio", "provisional")}
                </span>
              )}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-1">{L("Tuo netto stimato", "Your estimated net")}</span>
            <span className="h-10 flex items-center font-data text-lg font-bold text-primary" data-testid="self-calibration-net">{net != null ? `€${net}` : "—"}</span>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Button data-testid="self-calibration-save" size="sm" onClick={() => save(false)} disabled={saving || pct === ""}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}{L("Salva", "Save")}
            </Button>
            {data?.self_calibration_pct != null && (
              <Button data-testid="self-calibration-clear" size="sm" variant="outline" onClick={() => save(true)} disabled={saving}>
                {L("Rimuovi", "Clear")}
              </Button>
            )}
          </div>
        </div>
      )}
      {data?.self_price_provisional && !loading && (
        <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
          <Info className="w-3.5 h-3.5" />
          {L("Stiamo mostrando il tuo prezzo base configurato. Verrà sostituito con il prezzo reale pubblicato su Booking dopo la scansione del mercato (Competitor → Aggiorna mercato).",
            "Showing your configured base price. It will be replaced by the real published Booking price after a market scan (Competitor → Refresh market).")}
        </p>
      )}
    </motion.div>
  );
}
